import {
  makeStyles,
  tokens,
  Text,
  Body1,
  Caption1,
  Button,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  CheckmarkCircleFilled,
  DocumentBulletListRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useDiscoveryStore } from '../../stores/discoveryStore';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
    width: '100%',
    alignItems: 'center',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});

interface ReportDownloadProps {
  fileName: string;
}

export function ReportDownload({ fileName }: ReportDownloadProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const {
    reportObjectUrl,
    reportStored,
    sitesAnalysed,
    librariesAnalysed,
    filesAnalysed,
  } = useDiscoveryStore();

  // The report was returned inline (synchronous generation) and decoded to an
  // object URL — download straight from it, no second request to the function.
  const handleDownload = () => {
    if (!reportObjectUrl) return;
    const a = document.createElement('a');
    a.href = reportObjectUrl;
    a.download = fileName || 'Transformr-Readiness-Report.docx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className={styles.container}>
      <CheckmarkCircleFilled
        fontSize={48}
        color={tokens.colorPaletteGreenForeground1}
      />

      <Text size={600} weight="semibold">
        Your Report is Ready
      </Text>

      <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
        Your personalised Readiness report has been generated. Download it below
        to review your analysis and recommendations.
      </Body1>

      {(sitesAnalysed || librariesAnalysed || filesAnalysed) && (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {[
            sitesAnalysed != null && `${sitesAnalysed} sites`,
            librariesAnalysed != null && `${librariesAnalysed} libraries`,
            filesAnalysed != null && `${filesAnalysed} files`,
          ]
            .filter(Boolean)
            .join(' · ')}{' '}
          analysed
        </Caption1>
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="large"
          icon={<ArrowDownloadRegular />}
          onClick={handleDownload}
          disabled={!reportObjectUrl}
        >
          Download Report
        </Button>

        <Button
          appearance="subtle"
          icon={<DocumentBulletListRegular />}
          onClick={() => navigate('/reports')}
        >
          View Your Reports
        </Button>
      </div>

      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        {reportStored
          ? 'Your report has been saved. Access it anytime from the Reports page.'
          : 'Download your report now — it will not be kept unless you saved it.'}
        <br />
        File: {fileName}
      </Caption1>
    </div>
  );
}
